import React, { createContext, useContext, useState, useCallback, ReactNode, Suspense, lazy } from 'react';
import SToast, { ToastVariant } from '@/components/ui/molecules/SToast';
import { currentUiVersion } from '@/v2/lib/uiVersion';
import './ToastContext.css';

/* The v2 toast presentation. Lazy so a v1 session never downloads
   it — same rule as every other v2 chunk. The provider is mounted
   app-wide, so a static import here would pull v2 into the main
   bundle for everyone. */
const V2Toast = lazy(() =>
  import('@/v2/entry').then((m) => ({ default: m.V2Toast })),
);

export interface ToastConfig {
  variant: ToastVariant;
  title: string;
  message: string;
  ctaText?: string;
  onCtaClick?: () => void;
  duration?: number;
}

interface ToastContextType {
  showToast: (config: ToastConfig) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const hideToast = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setToast(null);
    }, 300); // Wait for fade out animation
  }, []);

  const showToast = useCallback(
    (config: ToastConfig) => {
      // Clear existing timeout if any
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Hide current toast if visible
      if (isVisible) {
        setIsVisible(false);
        setTimeout(() => {
          setToast(config);
          setIsVisible(true);
        }, 300);
      } else {
        setToast(config);
        setIsVisible(true);
      }

      // Auto hide after duration (default 5 seconds)
      const duration = config.duration ?? 5000;
      if (duration > 0) {
        const id = setTimeout(() => {
          hideToast();
        }, duration);
        setTimeoutId(id);
      }
    },
    [isVisible, timeoutId, hideToast]
  );

  /* The state and the timers are shared; only the presentation
     forks. Every `showToast()` call already in the v2 tree — and any
     added later — picks up the redesign without being touched.

     Read per render rather than held in state, matching UiRoute: the
     version cannot change mid-session by design. */
  const isV2 = currentUiVersion() === 'v2';

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}

      {toast && isV2 && (
        /* No fallback: a toast that arrives a beat late is fine, a
           placeholder box sliding in before it is not. */
        <Suspense fallback={null}>
          <V2Toast
            /* v1's config carries a title and a message; the redesign
               is a single line. The message is the specific half
               ("Max minutes per day exceeded — 120 min a day on your
               plan"), while the title mostly restates the tone the
               colour block already carries — so the message wins, and
               the title is the fallback when a caller passed only
               that. */
            tone={toast.variant}
            message={toast.message || toast.title}
            action={
              toast.ctaText && toast.onCtaClick
                ? { label: toast.ctaText, onClick: toast.onCtaClick }
                : undefined
            }
            open={isVisible}
            onClose={hideToast}
          />
        </Suspense>
      )}

      {toast && !isV2 && (
        <div className={`toast-container ${isVisible ? 'visible' : ''}`}>
          <SToast
            variant={toast.variant}
            title={toast.title}
            message={toast.message}
            onDismiss={hideToast}
          />
        </div>
      )}
    </ToastContext.Provider>
  );
};
